/**
 * Modal para compartir encuesta: link público y gestión de destinatarios externos (envío individual o masivo por mail).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Spinner } from './ui/spinner';
import { ExternalRecipientAutocomplete } from './ExternalRecipientAutocomplete';
import { RecipientListTable } from './RecipientListTable';
import { useAppDispatch } from '../store/hooks';
import {
  addRecipient,
  removeRecipient,
  clearRecipients,
  sendSingle,
} from '../features/surveys/assessmentRecipientsSlice';
import { Survey } from '../features/surveys/types';
import { AssessmentRecipientRow } from '../features/surveys/assessmentRecipientsApi';

interface ShareSurveyDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  selectedSurvey: Survey | null;
  // Link tab props (existing)
  shareLink?: string;
  shareError?: string;
  generatingToken?: boolean;
  expiresInDays?: number;
  onExpiresChange?: (v: number | undefined) => void;
  onGenerate?: () => void;
  onCopy?: () => void;
  canGenerateToken?: boolean;
  onClose?: () => void;
  // Destinatarios tab props (new)
  recipients?: AssessmentRecipientRow[];
  onAddRecipient?: (externalRecipientId: string) => void;
  onRemoveRecipient?: (recipientId: string) => void;
  onClearRecipients?: () => void;
  recipientsLoading?: boolean;
  recipientsError?: string;
  // Individual send state
  sendingSingle?: Record<string, boolean>;
  sendSingleResult?: Record<string, { success: boolean; message?: string }>;
  /** Envío masivo: genera tokens faltantes y envía mail a destinatarios pendientes (mismo flujo que API generate-mass + send-mass). */
  sendToAllLoading?: boolean;
  onSendToAllRecipients?: () => void;
}

function ExpirationInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        Días de expiración (opcional):
      </label>
      <input
        type="number"
        min="1"
        value={value || ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        placeholder="Sin expiración"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
      />
      <p className="text-xs text-muted-foreground mt-1">Deja vacío para que el link no expire</p>
    </div>
  );
}

export function ShareSurveyDialog({
  open,
  onOpenChange,
  selectedSurvey,
  shareLink = '',
  shareError = '',
  generatingToken = false,
  expiresInDays,
  onExpiresChange,
  onGenerate,
  onCopy,
  canGenerateToken = true,
  onClose,
  recipients = [],
  onAddRecipient,
  onRemoveRecipient,
  onClearRecipients,
  recipientsLoading = false,
  recipientsError = '',
  sendingSingle = {},
  sendSingleResult = {},
  sendToAllLoading = false,
  onSendToAllRecipients,
}: ShareSurveyDialogProps) {
  const dispatch = useAppDispatch();
  const { confirm } = useConfirmDialog();
  const [activeTab, setActiveTab] = useState<string>('link');
  const [localError, setLocalError] = useState<string>('');

  // Sync error from props
  useEffect(() => {
    if (shareError) setLocalError(shareError);
    else setLocalError('');
  }, [shareError]);

  // All hooks must be BEFORE any conditional return
  const handleSendSingle = useCallback((recipientId: string) => {
    if (!selectedSurvey) return;
    dispatch(sendSingle({ assessmentId: selectedSurvey.id, recipientId }));
  }, [dispatch, selectedSurvey]);

  const existingRecipientIds = useMemo(
    () => recipients.filter(Boolean).map((recipient) => recipient.externalRecipientId),
    [recipients],
  );

  if (!selectedSurvey) return null;

  const isSurveyCategory = selectedSurvey.category === 'survey';

  // --- Link Tab Content ---
  const renderLinkTab = () => {
    let body;
    if (localError) {
      body = (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
          {localError}
        </div>
      );
    } else if (generatingToken) {
      body = (
        <div className="flex items-center justify-center py-8">
          <Spinner />
          <span className="ml-2 text-muted-foreground">Generando link...</span>
        </div>
      );
    } else if (shareLink) {
      body = (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Link de la encuesta:</label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={shareLink}
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
              />
              <button
                onClick={onCopy}
                className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Copiar
              </button>
            </div>
          </div>
          <ExpirationInput value={expiresInDays} onChange={onExpiresChange!} />
          {canGenerateToken && onGenerate && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generatingToken}
              className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium border border-input bg-background hover:bg-muted disabled:opacity-50 transition-colors"
            >
              Regenerar link
            </button>
          )}
        </div>
      );
    } else {
      body = (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Configure la expiración opcional y genere el enlace. Cada enlace es de un solo uso: al recibir una
            respuesta queda invalidado y debe generarse uno nuevo si se necesita otro acceso.
          </p>
          <ExpirationInput value={expiresInDays} onChange={onExpiresChange!} />
          {canGenerateToken && onGenerate && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generatingToken || !selectedSurvey.active || selectedSurvey.status !== 'published'}
              className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {generatingToken ? (
                <>
                  <Spinner className="h-4 w-4" />
                  Generando…
                </>
              ) : (
                'Generar link'
              )}
            </button>
          )}
          {selectedSurvey.status !== 'published' && (
            <p className="text-xs text-amber-700">La encuesta debe estar publicada para generar un enlace.</p>
          )}
        </div>
      );
    }

    return (
      <div>
        {body}
        <DialogFooter className="mt-4">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
          >
            Cerrar
          </button>
        </DialogFooter>
      </div>
    );
  };

  // --- Destinatarios Tab Content ---
  const handleAddRecipient = (externalRecipientId: string) => {
    if (onAddRecipient) {
      onAddRecipient(externalRecipientId);
    } else {
      dispatch(addRecipient({ assessmentId: selectedSurvey.id, externalRecipientId }));
    }
  };

  const handleRemoveRecipient = (recipientId: string) => {
    if (onRemoveRecipient) {
      onRemoveRecipient(recipientId);
    } else {
      dispatch(removeRecipient({ assessmentId: selectedSurvey.id, recipientId }));
    }
  };

  const handleClearRecipients = async () => {
    const ok = await confirm({
      title: 'Quitar destinatarios',
      message: '¿Estás seguro de que quieres quitar todos los destinatarios?',
      confirmText: 'Quitar todos',
      variant: 'danger',
    });
    if (!ok) return;
    if (onClearRecipients) {
      onClearRecipients();
    } else {
      dispatch(clearRecipients(selectedSurvey.id));
    }
  };

  const handleSendToAllRecipients = async () => {
    if (!onSendToAllRecipients || recipients.length === 0) return;
    const ok = await confirm({
      title: 'Enviar a destinatarios',
      message:
        'Se generarán enlaces personales si faltan y se enviará un email a cada destinatario que aún no recibió el acceso. ¿Continuar?',
      confirmText: 'Enviar',
    });
    if (!ok) return;
    onSendToAllRecipients();
  };

  const renderDestinatariosTab = () => {
    if (recipientsLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Spinner />
          <span className="ml-2 text-muted-foreground">Cargando destinatarios...</span>
        </div>
      );
    }

    const canMassSend =
      Boolean(onSendToAllRecipients) &&
      canGenerateToken &&
      selectedSurvey.active &&
      recipients.length > 0 &&
      !recipientsLoading &&
      !sendToAllLoading;

    return (
      <div className="space-y-4">
        {/* Autocomplete to add recipients */}
        <ExternalRecipientAutocomplete
          onSelect={handleAddRecipient}
          existingRecipientIds={existingRecipientIds}
        />

        {onSendToAllRecipients && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-3 space-y-2">
            <p className="text-sm text-foreground font-medium">Envío por email a todos</p>
            <p className="text-xs text-muted-foreground">
              Genera tokens que falten y envía un correo a quienes aún no tienen el mail enviado. Los que ya recibieron
              el acceso no se duplican.
            </p>
            <button
              type="button"
              onClick={handleSendToAllRecipients}
              disabled={!canMassSend}
              className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              {sendToAllLoading ? (
                <>
                  <Spinner className="h-4 w-4" />
                  Enviando a todos…
                </>
              ) : (
                'Enviar mail a todos los destinatarios'
              )}
            </button>
            {!selectedSurvey.active && (
              <p className="text-xs text-amber-700">La encuesta debe estar activa para enviar accesos.</p>
            )}
          </div>
        )}

        {/* Recipient list table */}
        <RecipientListTable
          assessmentId={selectedSurvey.id}
          recipients={recipients}
          onRemove={handleRemoveRecipient}
          onClear={handleClearRecipients}
          onSend={handleSendSingle}
          sendingSingle={sendingSingle}
          sendSingleResult={sendSingleResult}
        />

        {recipientsError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
            {recipientsError}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Compartir Encuesta</DialogTitle>
          <DialogDescription>
            <strong>{selectedSurvey.title}</strong>
            <br />
            {activeTab === 'link'
              ? 'Genera un link de un solo uso para compartir esta encuesta.'
              : 'Gestiona los destinatarios externos y envía accesos masivos.'}
          </DialogDescription>
        </DialogHeader>

        {isSurveyCategory && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2">
            <TabsList>
              <TabsTrigger value="link">Link</TabsTrigger>
              <TabsTrigger value="destinatarios">Destinatarios</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div className="mt-2">
          {activeTab === 'link' && renderLinkTab()}
          {activeTab === 'destinatarios' && renderDestinatariosTab()}
        </div>
      </DialogContent>
    </Dialog>
  );
}